import { TellescopeModelFromName, useUsersMap } from "@tellescope/tanstack-query";
import { cn, Tooltip, TooltipContent, TooltipTrigger, useCurrentTimestamp } from "@tellescope/ui";
import { formatName } from "@tellescope/utils";
import { addMinutes } from "date-fns/addMinutes";
import { Calendar, Clock, User } from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getZonedDayStart } from "../../utils";
import { useDateFnsFormattersWithLocale } from "@tellescope/ui";

export interface OngoingOrUpcomingEventProps {
  event: TellescopeModelFromName<"calendar_events">;
  timezone?: string;
}

export const OngoingOrUpcomingEvent = ({
  event,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: OngoingOrUpcomingEventProps) => {
  const { t } = useTranslation("calendar");
  const { format, formatInTimeZone } = useDateFnsFormattersWithLocale();
  const eventStartDate = useMemo(() => new Date(event.startTimeInMS), [event.startTimeInMS]);
  const eventEndDate = useMemo(
    () => addMinutes(eventStartDate, event.actualDuration ?? event.durationInMinutes),
    [event.actualDuration, event.durationInMinutes, eventStartDate]
  );
  const endsInDifferentDay = useMemo(() => {
    const eventStartDateInTimezone = getZonedDayStart(eventStartDate, timezone).getTime();
    const eventEndDateInTimezone = getZonedDayStart(eventEndDate, timezone).getTime();
    return eventStartDateInTimezone !== eventEndDateInTimezone;
  }, [eventEndDate, eventStartDate, timezone]);
  const currentTimestamp = useCurrentTimestamp({ pollingRateInMs: 1000 * 60, snapTo: "minute" });

  const userMap = useUsersMap();
  const userNames = useMemo(
    () =>
      event.attendees
        .reduce<string[]>((acc, curr) => {
          if (curr.type === "enduser") {
            return acc;
          }

          const user = userMap[curr.id];
          if (!user) {
            acc.push(t("User: {{id}}", { id: curr.id }));
          } else {
            acc.push(formatName(user));
          }

          return acc;
        }, [])
        .join(", "),
    [event.attendees, t, userMap]
  );

  const isUpcoming = event.startTimeInMS > currentTimestamp.getTime();

  return (
    <div className={cn("flex flex-col gap-1.5 font-light text-sm text-muted-foreground text-left")}>
      <p className={cn("font-semibold text-primary line-clamp-2", isUpcoming && "text-muted-foreground")}>
        {event.displayTitle || event.title}
      </p>
      <div className="flex gap-2 items-center">
        <Tooltip>
          <TooltipTrigger>
            <User size={14} />
          </TooltipTrigger>
          <TooltipContent>{t("Users")}</TooltipContent>
        </Tooltip>
        {userNames}
      </div>
      <div className="flex gap-2 items-center">
        <Calendar size={14} />
        {format(new Date(event.startTimeInMS), "MMM d, yyyy")}
      </div>
      <div className="flex gap-2 items-center">
        <Clock size={14} />
        {t("{{startTime}} - {{endTime}}", {
          startTime: formatInTimeZone(eventStartDate, timezone, "h:mm a"),
          endTime: formatInTimeZone(eventEndDate, timezone, endsInDifferentDay ? "h:mm a (MMM d, yyyy)" : "h:mm a"),
        })}
      </div>
    </div>
  );
};
